import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from '@tanstack/react-router';
import { keepPreviousData } from '@tanstack/react-query';
import { ArrowRight, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  getListClientsQueryKey,
  useListClients,
} from '@biasafe.queries/clients/clients';
import {
  getListStrategiesQueryKey,
  useListStrategies,
} from '@biasafe.queries/strategies/strategies';
import {
  getSearchInstrumentsQueryKey,
  useSearchInstruments,
} from '@biasafe.queries/portfolio-instruments-search/portfolio-instruments-search';
import {
  buildSearchItems,
  rankItems,
  referenceKey,
  resolveReference,
  scopeKind,
  searchReference,
  securityItem,
} from '../model/searchModel';
import { useSearchLibrary } from '../model/useSearchLibrary';
import {
  buildResultGroups,
  emptyFilters,
  flattenResultGroups,
} from '../model/searchResults';
import { securitySearchTerm } from '../config/commands';
import { SearchPreview } from './SearchPreview';
import { SearchInstrumentFilters } from './SearchInstrumentFilters';
import { SearchResultRow } from './SearchResultRow';
import type { SearchItem, SearchScope } from '../model/searchModel';
import { retryInstrumentSearch } from '@/features/Instruments/searchRetry';
import {
  instrumentExchange,
  instrumentVenue,
} from '@/features/Instruments/instrumentMetadata';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useOnClickOutside } from '@/shared/hooks/useOnClickOutside';

const scopes: Array<SearchScope> = [
  'all',
  'functions',
  'clients',
  'strategies',
  'securities',
];
/** One ranked command bar: no account selection, order mutation or quote-per-row fan-out. */
export function HeaderSearch() {
  const navigate = useNavigate();
  const { t } = useTranslation('app-shell');
  const text = (key: string, fallback: string) =>
    t(`header.${key}`, { defaultValue: fallback });
  const [query, setQuery] = useState('');
  const [term, setTerm] = useState('');
  const [scope, setScope] = useState<SearchScope>('all');
  const [isExpanded, setExpanded] = useState(false);
  const [active, setActive] = useState(0);
  const [width, setWidth] = useState(880);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState(emptyFilters);
  const [preview, setPreview] = useState(false);
  const [focusedKey, setFocusedKey] = useState<string>();
  const [context, setContext] =
    useState<Extract<SearchItem, { kind: 'security' }>>();
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const library = useSearchLibrary();
  useLayoutEffect(() => {
    const measure = () =>
      setWidth(
        Math.min(
          880,
          Math.max(
            0,
            window.innerWidth -
              (window.innerWidth < 640
                ? 16
                : (containerRef.current?.getBoundingClientRect().left ?? 0)) -
              16,
          ),
        ),
      );
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isExpanded]);
  const clientsQ = useListClients(undefined, {
    query: {
      queryKey: getListClientsQueryKey(),
      enabled:
        isExpanded && (scope === 'all' || scope === 'clients' || !query.trim()),
      staleTime: 60_000,
      retry: false,
    },
  });
  const strategiesQ = useListStrategies(undefined, {
    query: {
      queryKey: getListStrategiesQueryKey(),
      enabled:
        isExpanded &&
        (scope === 'all' || scope === 'strategies' || !query.trim()),
      staleTime: 60_000,
      retry: false,
    },
  });
  useEffect(() => {
    const timer = setTimeout(() => setTerm(securitySearchTerm(query)), 200);
    return () => clearTimeout(timer);
  }, [query]);
  const securityScope = scope === 'all' || scope === 'securities';
  const contextCommand = ['DES', 'QUOTE'].includes(query.trim().toUpperCase());
  const securitiesQ = useSearchInstruments(
    { q: term },
    {
      query: {
        queryKey: getSearchInstrumentsQueryKey({ q: term }),
        enabled: isExpanded && securityScope && !!term && !contextCommand,
        staleTime: 60_000,
        retry: retryInstrumentSearch,
        retryDelay: 1000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        placeholderData: keepPreviousData,
      },
    },
  );
  const items = useMemo(
    () =>
      buildSearchItems(
        clientsQ.data?.clients ?? [],
        strategiesQ.data?.strategies ?? [],
        securitiesQ.data?.matches ?? [],
      ),
    [clientsQ.data, strategiesQ.data, securitiesQ.data],
  );
  const favorites = library.favorites
    .map((r) => resolveReference(r, items))
    .filter((r): r is SearchItem => !!r);
  const recents = library.recent
    .map((r) => resolveReference(r, items))
    .filter((r): r is SearchItem => !!r);
  const favoriteKeys = new Set(library.favorites.map(referenceKey));
  const ranked = useMemo(() => {
    if (contextCommand) return context ? [context] : [];
    const source =
      scope === 'securities'
        ? buildSearchItems(
            [],
            [],
            (securitiesQ.data?.matches ?? []).filter(
              (i) =>
                (filters.country === 'All' ||
                  instrumentVenue(i)?.country === filters.country) &&
                (filters.type === 'All' || i.securityType === filters.type) &&
                (filters.exchange === 'All' ||
                  instrumentExchange(i) === filters.exchange),
            ),
          )
        : items;
    const all = rankItems(source, query);
    return scope === 'all'
      ? all
      : all.filter((i) => i.kind === scopeKind[scope]);
  }, [items, query, scope, filters, contextCommand, context, securitiesQ.data]);
  const groupLabel = (kind: SearchItem['kind']) =>
    kind === 'command'
      ? text('search_group_commands', 'Functions & pages')
      : kind === 'client'
        ? text('search_group_clients', 'Clients')
        : kind === 'strategy'
          ? text('search_group_strategies', 'Strategies')
          : text('search_group_securities', 'Securities');
  const groups = buildResultGroups({
    query,
    scope,
    ranked,
    items,
    favorites,
    recents,
    labels: {
      best: text('search_best', 'Best match'),
      favorites: text('search_favorites', 'Pinned'),
      recent: text('search_recent', 'Recently opened'),
      frequent: text('search_frequent', 'Frequent functions'),
      categories: {
        command: groupLabel('command'),
        client: groupLabel('client'),
        strategy: groupLabel('strategy'),
        security: groupLabel('security'),
      },
    },
  });
  const flattened = flattenResultGroups(groups, expandedGroups);
  const selected =
    (focusedKey && flattened.find((item) => item.key === focusedKey)) ||
    flattened.at(Math.min(active, Math.max(0, flattened.length - 1)));
  const choose = (index: number) => {
    setActive(index);
    setFocusedKey(flattened.at(index)?.key);
  };
  useEffect(() => {
    setActive(0);
    setFocusedKey(undefined);
    setExpandedGroups(new Set());
  }, [query, scope, filters]);
  useEffect(() => {
    if (!isExpanded || !selected) return;
    const row = document.getElementById(`${listId}-${selected.key}`);
    if (row && typeof row.scrollIntoView === 'function')
      row.scrollIntoView({ block: 'nearest' });
  }, [isExpanded, selected?.key, listId]);
  const close = () => {
    setExpanded(false);
    setQuery('');
    setTerm('');
    setScope('all');
    setFilters(emptyFilters);
    setPreview(false);
    setActive(0);
    triggerRef.current?.focus();
  };
  function open(item: SearchItem) {
    library.remember(item);
    if (item.kind === 'security') {
      setContext(item);
      void navigate({
        to: '/instruments/$conid',
        params: { conid: String(item.instrument.conid) },
      });
    } else if (item.kind === 'client')
      void navigate({
        to: '/clients/$clientId',
        params: { clientId: item.client.id },
      });
    else if (item.kind === 'strategy')
      void navigate({
        to: '/strategies/$strategyId/report',
        params: { strategyId: item.strategyId },
      });
    else if (item.destination.to === '/execution')
      void navigate({
        to: '/execution',
        search: {
          tab: 'tab' in item.destination ? item.destination.tab : 'orders',
        },
      });
    else if (item.destination.to === '/settings' && 'tab' in item.destination)
      void navigate({ to: '/settings', search: { tab: item.destination.tab } });
    else void navigate({ to: item.destination.to });
    close();
  }
  const toggleGroup = (key: string, expand?: boolean) =>
    setExpandedGroups((before) => {
      const next = new Set(before);
      if (expand === true || (!next.has(key) && expand !== false))
        next.add(key);
      else next.delete(key);
      return next;
    });
  useOnClickOutside(containerRef, (e) => {
    if (
      e.target instanceof Element &&
      e.target.closest('[data-radix-popper-content-wrapper]')
    )
      return;
    if (isExpanded) close();
  });
  useEffect(() => {
    if (isExpanded) {
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [isExpanded]);
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setExpanded(true);
        inputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, []);
  const loadingSecurities =
    securityScope &&
    !!query.trim() &&
    !contextCommand &&
    (securitiesQ.isFetching || term !== securitySearchTerm(query));
  const staleSecurities =
    !!query.trim() &&
    (securitiesQ.isPlaceholderData || term !== securitySearchTerm(query));
  function setCategory(next: SearchScope) {
    setScope(next);
    setPreview(false);
    inputRef.current?.focus();
  }
  let rowIndex = 0;
  const renderItem = (item: SearchItem) => {
    const index = rowIndex++;
    return (
      <SearchResultRow
        key={item.key}
        item={item}
        id={`${listId}-${item.key}`}
        active={selected?.key === item.key}
        onHover={() => choose(index)}
        onOpen={() => open(item)}
        onToggle={() => {
          choose(index);
          toggleGroup(item.key);
        }}
        expanded={expandedGroups.has(item.key)}
        pinned={favoriteKeys.has(referenceKey(searchReference(item)))}
        canPin={library.enabled}
        onPin={() => library.toggle(item)}
        onPreview={() => {
          choose(index);
          setPreview((v) => (selected?.key === item.key ? !v : true));
        }}
      />
    );
  };
  return (
    <div
      ref={containerRef}
      className="relative z-50 flex items-center"
      onKeyDown={(event) => {
        if (event.defaultPrevented || event.key !== 'Escape') return;
        event.preventDefault();
        if (preview) {
          setPreview(false);
          inputRef.current?.focus();
        } else close();
      }}
    >
      <Button
        ref={triggerRef}
        variant="ghost"
        size="sm"
        className={cn(
          'h-10 w-10 rounded-full p-0',
          isExpanded ? 'pointer-events-none opacity-0' : 'bg-muted/40',
        )}
        onClick={() => setExpanded(true)}
        aria-label={t('header.search_open')}
        aria-expanded={isExpanded}
      >
        <Search className="size-4.5" />
      </Button>
      <div
        style={{ width }}
        className={cn(
          'fixed left-4 top-2 z-50 overflow-hidden rounded-lg border border-border/80 bg-popover shadow-lg shadow-foreground/8 focus-within:border-primary/40 sm:absolute sm:left-0 sm:top-0',
          !isExpanded && 'pointer-events-none invisible',
        )}
      >
        <div className="relative flex h-11 items-center border-b border-border/60 bg-popover">
          <Search className="absolute left-3 size-4 text-primary" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={text(
              'search_placeholder',
              'Search functions, pages, clients, strategies or securities…',
            )}
            aria-label={t('header.search_label')}
            role="combobox"
            aria-expanded={isExpanded}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={
              selected ? `${listId}-${selected.key}` : undefined
            }
            className="h-11 rounded-none border-0 bg-transparent pl-10 pr-10 text-sm focus-visible:ring-0"
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                if (preview) setPreview(false);
                else close();
                return;
              }
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                const current = flattened.findIndex(
                  (item) => item.key === selected?.key,
                );
                choose(
                  flattened.length
                    ? (current +
                        (e.key === 'ArrowDown' ? 1 : -1) +
                        flattened.length) %
                        flattened.length
                    : 0,
                );
                return;
              }
              if (
                e.key === 'ArrowLeft' &&
                selected?.kind === 'security' &&
                selected.child
              ) {
                const parent = flattened.find(
                  (item) =>
                    item.kind === 'security' &&
                    item.children.some(
                      (child) => child.conid === selected.instrument.conid,
                    ),
                );
                if (parent) {
                  e.preventDefault();
                  toggleGroup(parent.key, false);
                  setFocusedKey(parent.key);
                  return;
                }
              }
              if (e.key === 'Enter' && selected) {
                e.preventDefault();
                open(selected);
                return;
              }
              if (
                e.altKey &&
                (e.key.toLowerCase() === 'p' || e.code === 'KeyP') &&
                selected
              ) {
                e.preventDefault();
                library.toggle(selected);
                return;
              }
              if (e.key === ' ' && selected && e.altKey) {
                e.preventDefault();
                setPreview((v) => !v);
                return;
              }
              if (
                (e.key === 'ArrowRight' || e.key === 'ArrowLeft') &&
                selected?.kind === 'security' &&
                selected.children.length
              ) {
                e.preventDefault();
                toggleGroup(selected.key, e.key === 'ArrowRight');
              }
            }}
          />
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-1 size-7 rounded-full p-0"
            onClick={close}
            aria-label={t('header.search_close')}
          >
            <X className="size-4" />
          </Button>
        </div>
        {isExpanded && (
          <div className="flex max-h-[min(720px,calc(100dvh-5rem))] flex-col overflow-hidden bg-popover text-popover-foreground">
            <div
              className="z-20 flex shrink-0 flex-wrap gap-1 border-b border-border/50 bg-popover px-2 py-1.5"
              role="group"
              aria-label={text('search_categories', 'Search categories')}
            >
              {scopes.map((s) => (
                <button
                  key={s}
                  type="button"
                  aria-pressed={scope === s}
                  onClick={() => setCategory(s)}
                  className={cn(
                    'rounded px-3 py-1.5 text-xs font-medium focus-visible:outline-2 focus-visible:outline-ring',
                    scope === s
                      ? 'bg-primary/8 text-primary'
                      : 'text-muted-foreground hover:bg-primary/3',
                  )}
                >
                  {text(
                    `search_scope_${s}`,
                    s === 'functions'
                      ? 'Pages & functions'
                      : s === 'securities'
                        ? 'Securities'
                        : s[0].toUpperCase() + s.slice(1),
                  )}
                </button>
              ))}
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {scope === 'securities' && query.trim() && (
                <SearchInstrumentFilters
                  allInstruments={securitiesQ.data?.matches ?? []}
                  filters={filters}
                  count={ranked.length}
                  onChange={setFilters}
                />
              )}
              {loadingSecurities && (
                <p
                  role="status"
                  className="px-3 py-2 text-xs text-muted-foreground"
                >
                  {securitiesQ.failureCount > 0
                    ? text('search_reconnecting', 'Reconnecting to IBKR…')
                    : staleSecurities
                      ? text('search_updating', 'Updating results…')
                      : text('search_ibkr_loading', 'Searching IBKR…')}
                </p>
              )}
              {!query.trim() && library.recent.length > 0 && (
                <div className="flex justify-end px-3 pt-2">
                  <button
                    type="button"
                    className="text-xs text-muted-foreground hover:text-foreground"
                    onClick={library.clear}
                  >
                    {text('search_clear_recent', 'Clear history')}
                  </button>
                </div>
              )}
              <div
                id={listId}
                role="listbox"
                aria-label={text('search_results', 'Search results')}
                aria-busy={loadingSecurities}
              >
                {groups.map((group) =>
                  group.items.length > 0 ? (
                    <section
                      key={group.key}
                      className="border-t border-border/40 pb-1 first:border-t-0"
                    >
                      <div className="flex items-center justify-between gap-3 px-4 pb-1 pt-2">
                        <h3
                          className={cn(
                            'text-[11px] font-medium leading-4 text-muted-foreground',
                            group.key === 'best' && 'text-primary',
                          )}
                        >
                          {group.label}
                        </h3>
                      </div>
                      {group.items.flatMap((item) =>
                        item.kind === 'security' && expandedGroups.has(item.key)
                          ? [
                              renderItem(item),
                              ...item.children.map((c) =>
                                renderItem(securityItem(c, [], true)),
                              ),
                            ]
                          : [renderItem(item)],
                      )}
                      {scope === 'all' &&
                        query.trim() &&
                        group.scope &&
                        !!group.total && (
                          <button
                            type="button"
                            onClick={() => setCategory(group.scope!)}
                            className="ml-[140px] flex items-center gap-1 rounded px-2 py-1.5 text-xs text-primary hover:bg-primary/3 focus-visible:outline-2 focus-visible:outline-ring max-sm:ml-12"
                          >
                            {group.scope === 'securities'
                              ? text(
                                  'search_view_securities',
                                  'View all securities',
                                )
                              : text(
                                  `search_view_${group.scope}`,
                                  `View all ${group.scope}`,
                                )}
                            <span className="text-muted-foreground">
                              ({group.total})
                            </span>
                            <ArrowRight className="size-3" />
                          </button>
                        )}
                    </section>
                  ) : null,
                )}
              </div>
              {scope === 'all' &&
                query.trim() &&
                ranked.at(0)?.kind === 'security' &&
                !groups.some((g) => g.key === 'security' && g.items.length) && (
                  <button
                    type="button"
                    onClick={() => setCategory('securities')}
                    className="ml-[140px] flex items-center gap-1 rounded px-2 py-1.5 text-xs text-primary hover:bg-primary/3 max-sm:ml-12"
                  >
                    {text('search_view_securities', 'View all securities')}
                    <ArrowRight className="size-3" />
                  </button>
                )}
              {(scope === 'all' || scope === 'clients') &&
                clientsQ.isLoading && (
                  <p
                    role="status"
                    className="px-3 py-3 text-xs text-muted-foreground"
                  >
                    {text('search_clients_loading', 'Loading clients…')}
                  </p>
                )}
              {(scope === 'all' || scope === 'strategies') &&
                strategiesQ.isLoading && (
                  <p
                    role="status"
                    className="px-3 py-3 text-xs text-muted-foreground"
                  >
                    {text('search_strategies_loading', 'Loading strategies…')}
                  </p>
                )}
              {(scope === 'all' || scope === 'clients') && clientsQ.isError && (
                <p
                  role="status"
                  className="px-3 py-3 text-xs text-muted-foreground"
                >
                  {text('search_clients_error', 'Clients unavailable.')}{' '}
                  <button
                    className="underline"
                    onClick={() => void clientsQ.refetch()}
                  >
                    {text('search_retry', 'Retry')}
                  </button>
                </p>
              )}
              {(scope === 'all' || scope === 'strategies') &&
                strategiesQ.isError && (
                  <p
                    role="status"
                    className="px-3 py-3 text-xs text-muted-foreground"
                  >
                    {text('search_strategies_error', 'Strategies unavailable.')}{' '}
                    <button
                      className="underline"
                      onClick={() => void strategiesQ.refetch()}
                    >
                      {text('search_retry', 'Retry')}
                    </button>
                  </p>
                )}
              {securityScope &&
                query.trim() &&
                !contextCommand &&
                securitiesQ.isError && (
                  <p
                    role="status"
                    className="px-3 py-3 text-xs text-muted-foreground"
                  >
                    {text('search_ibkr_error', 'IBKR connection unavailable.')}{' '}
                    <button
                      className="underline"
                      onClick={() => void securitiesQ.refetch()}
                    >
                      {text('search_retry', 'Retry')}
                    </button>
                  </p>
                )}
              {query.trim() &&
                !flattened.length &&
                !loadingSecurities &&
                !(
                  (scope === 'all' || scope === 'clients') &&
                  (clientsQ.isLoading || clientsQ.isError)
                ) &&
                !(
                  (scope === 'all' || scope === 'strategies') &&
                  (strategiesQ.isLoading || strategiesQ.isError)
                ) &&
                !(securityScope && !contextCommand && securitiesQ.isError) && (
                  <p
                    role="status"
                    className="px-3 py-6 text-center text-sm text-muted-foreground"
                  >
                    {contextCommand && !context
                      ? text(
                          'search_select_security',
                          'Select a security first, then use DES or QUOTE.',
                        )
                      : text('search_no_matches', 'No results.')}
                  </p>
                )}
              {!query.trim() && !favorites.length && !recents.length && (
                <p className="px-3 py-3 text-xs text-muted-foreground">
                  {text(
                    'search_library_hint',
                    'Open a result to find it here next time. Use the star to pin a favorite.',
                  )}
                </p>
              )}
            </div>
            {preview && selected && (
              <SearchPreview
                key={selected.key}
                item={selected}
                onClose={() => setPreview(false)}
                onOpen={() => open(selected)}
              />
            )}
            <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border/50 bg-muted/15 px-4 py-2 text-[11px] text-muted-foreground">
              <span
                title={text(
                  'search_keyboard',
                  '↑ ↓ select · Enter open · → listings · Alt Space preview · Alt P pin',
                )}
              >
                {text(
                  'search_keyboard_short',
                  '↑ ↓ Navigate · Enter Open · Esc Close',
                )}
              </span>
              <span className="hidden sm:inline">
                {text('search_personal', 'Personal history')}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
